import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Body2, Button, Caption1, ConfirmDialog, Dialog } from "@activecollab/components";
import classNames from "classnames";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { Divider } from "~/components/ModalObjects";
import StepLoader from "~/pages/GettingStarted/StepLoader";
import { UserType } from "~/records/record.user";
import { useBatchCreateVirtualUsers } from "~/repository";
import { useCompleteStepMutation } from "~/repository/useGettingStarted";
import { loadUsersWorkweekCapacities } from "~/store/actions/actions.users";
import { getConfigOption, getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import { getActiveOwnersAndMembers } from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import { sortCompare } from "~/utils/utils";
import { validationAddYourTeamSchema } from "~/validators";
import AddYourTeamFormElements from "./AddYourTeamFormElements";
import { GettingStartedDialog } from "./GettingStartedDialog";
import { TeamMembersList } from "./TeamMembersList";

export const AddYourTeamDialog = ({ open, onClose, isAlreadyCompleted }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef();
  const [newMembers, setNewMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const batchVirtualUsersCreateMutation = useBatchCreateVirtualUsers();
  const completeStepMutation = useCompleteStepMutation();
  const systemWeeklyWorkerCapacity = useSelector((state) => getConfigOption(state, "weekly_worker_capacity"))?.toJS();
  const activeUsers = useSelector(getActiveOwnersAndMembers);
  const ownerCompanyId = useSelector((state) => getRawConfigOption(state, "owner_company_id"));
  const showTestimonials = useSelector((state) => !getFeatureFlag(state, "why_this_matters_off"));

  const initialValues = useMemo(
    () => ({
      first_name: "",
      last_name: "",
      job_type_ids: [],
      role: UserType.MEMBER,
      weekly_worker_capacity: [...systemWeeklyWorkerCapacity],
    }),
    [systemWeeklyWorkerCapacity]
  );

  const onSave = useCallback(
    (v) => {
      let data = newMembers.map(({ uuid, name, ...rest }) => rest);

      if (v.first_name.trim() && v.last_name.trim()) {
        data.push(v);
      }

      data = data.map((i) => ({ ...i, company_id: ownerCompanyId }));
      batchVirtualUsersCreateMutation.mutate(data, {
        onSuccess: () => {
          Angie.logger.info(
            "Getting started step completed.",
            { step: "setup_your_team", reopen: isAlreadyCompleted ? 1 : 0 },
            true
          );
          completeStepMutation.mutate("getting_started_setup_team");
          onClose(false);
        },
      });
    },
    [batchVirtualUsersCreateMutation, completeStepMutation, isAlreadyCompleted, newMembers, onClose, ownerCompanyId]
  );

  const users = activeUsers
    .map((user) => {
      return {
        id: user.id,
        name: user.display_name,
        image: user.avatar_url.replace("--SIZE--", "24"),
      };
    })
    .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));

  useEffect(() => {
    setIsLoading(true);
    dispatch(loadUsersWorkweekCapacities()).then(() => {
      setIsLoading(false);
    });
  }, [dispatch]);

  useUpdateEffect(() => {
    if (newMembers.length === 0) {
      formikRef.current.validateForm();
      formikRef.current.setTouched({});
    }
  }, [newMembers]);

  const handleClick = useCallback((id) => {
    setSelectedUserId(id);
  }, []);

  const handleRemoveUser = useCallback(() => {
    setNewMembers((prevUsers) => prevUsers.filter((user) => user.uuid !== selectedUserId));
    setSelectedUserId(null);
  }, [selectedUserId]);

  const handleChange = useCallback((user) => {
    setNewMembers((prevUsers) => {
      const userIndex = prevUsers.findIndex((u) => u.uuid === user.uuid);
      if (userIndex !== -1) {
        const updatedUsers = [...prevUsers];
        updatedUsers[userIndex] = user;
        return updatedUsers;
      }
      return prevUsers;
    });
  }, []);

  return (
    <GettingStartedDialog
      dialogClassName={classNames("tw-p-2 sm:tw-p-6 lg:tw-p-4", {
        "lg:tw-pl-10": showTestimonials,
        "lg:tw-px-6": !showTestimonials,
      })}
      open={open}
      onClose={onClose}
      dialogTitle={t("Add Your Team")}
      fullDialogWidth={showTestimonials ? "lg:tw-w-[960px]" : ""}
      rightSideClassName="lg:tw-max-w-[400px]"
      featureExplainerTitle={t("Why this matters")}
      featureExplainerContent={
        <div
          data-testid="explainer-content"
          className="tw-flex tw-flex-col tw-w-full tw-gap-8 tw-mt-4 "
        >
          <Body2>
            {t(
              "Having ActiveCollab as a single source of truth for your entire team will help you improve productivity and collaboration, while minimizing context switching."
            )}
          </Body2>
          <Body2>
            {t(
              " You’ll monitor team availability, plan capacity, and distribute workload effectively to prevent burnout and ensure project success. With live commenting and chat, you’ll share updates in real time, making sure everyone’s informed and in sync."
            )}
          </Body2>
        </div>
      }
      testimonialImageUrl="onboarding/images/EricBurns.png"
      testimonialImageAlt="Eric Burns"
      testimonialContent={
        <div data-testid="testimonial-content">
          <Body2 italic>
            &quot;
            {t(
              "All of our teams use ActiveCollab daily and around the clock to know the status of features / tasks + keep track of time! We LOVE ActiveCollab!"
            )}
            &quot;
          </Body2>
          <Body2
            weight="bold"
            className="tw-mt-4"
          >
            Eric Burns
          </Body2>
          <Body2>CEO, Gazella</Body2>
        </div>
      }
    >
      {isLoading ? (
        <StepLoader className="tw-mt-6 lg:tw-mt-0 lg:tw-w-[440px]" />
      ) : (
        <div
          className={classNames("tw-mt-2 lg:tw-mt-0 lg:tw-h-full lg:tw-w-[440px]", {
            "tw-h-[300px]": showTestimonials,
          })}
        >
          <Caption1 weight="bold">{t("Already In")}</Caption1>
          <TeamMembersList
            users={users}
            className="tw-my-2"
            isLoading={isLoading}
          />
          <Body2 className={classNames({ "tw-mb-2": !newMembers.length })}>
            {t(
              "Add a team member to see what it looks like. We won't be sending them any invitations—you can do that in the People section later."
            )}
          </Body2>
          <TeamMembersList
            users={newMembers}
            className="tw-my-2"
            onClick={handleClick}
            onChange={handleChange}
          />
          {newMembers.length ? <Divider className="tw-my-0" /> : null}
          <Formik
            initialValues={initialValues}
            innerRef={formikRef}
            validationSchema={validationAddYourTeamSchema(newMembers)}
            onSubmit={onSave}
            validateOnMount
          >
            {({ handleSubmit, errors, isSubmitting }) => {
              const handleFormSubmit = (e) => {
                e.preventDefault();
                handleSubmit();
                if (Object.keys(errors)?.length) {
                  return focusFirstInvalidField(errors, true);
                }
              };
              return (
                <Form
                  className={classNames("tw-flex tw-flex-col tw-mt-8 lg:tw-w-[440px]", {
                    "tw-mt-4": !!newMembers.length,
                  })}
                  onSubmit={handleFormSubmit}
                >
                  <AddYourTeamFormElements
                    newMembers={newMembers}
                    setNewMembers={setNewMembers}
                    fieldRef={fieldRef}
                    focusFirstInvalidField={focusFirstInvalidField}
                  />

                  <Dialog.ContentDivider className="tw-mt-4" />
                  <Dialog.Actions className="tw-pl-0 tw-pb-4 lg:tw-pb-0">
                    <Button
                      type="submit"
                      variant="primary"
                      className="tw-mr-3"
                      disabled={isSubmitting}
                    >
                      {t("Save")}
                    </Button>
                    <Button
                      type="button"
                      onClick={onClose}
                      variant="secondary"
                      disabled={isSubmitting}
                    >
                      {t("Cancel")}
                    </Button>
                  </Dialog.Actions>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}

      <ConfirmDialog
        open={!!selectedUserId}
        onCancel={() => setSelectedUserId(null)}
        onConfirm={handleRemoveUser}
        dialogTitle={t("Remove Team Member")}
        dialogContent={t("Are you sure that you want to remove this user?")}
      />
    </GettingStartedDialog>
  );
};

AddYourTeamDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  isAlreadyCompleted: PropTypes.bool,
};
